import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { format } from 'date-fns';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  DollarSign,
  Globe,
  Mail,
  Megaphone,
  MousePointerClick,
  Pencil,
  Plus,
  RefreshCw,
  Share2,
  Target,
  Trash2,
  TrendingUp,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { db } from '../firebase';
import BBALoader from '../components/BBALoader';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';
import { FIELD_CLASS, LABEL_CLASS, TH_CLASS } from '../components/crm/CrmFilterUi';
import { useTheme } from '../contexts/ThemeContext';
import {
  addMarketingReportEntry,
  deleteMarketingReportEntry,
  updateMarketingReportEntry,
  DATA_TYPES,
} from '../services/marketingReportsService';
import { getSharedNewsletterData } from '../services/newsletterService';
import {
  SOCIAL_PLATFORMS,
  TIMEFRAMES,
  adSeries,
  adSpendOf,
  bucketUnit,
  channelFor,
  dedupeDaily,
  getRange,
  groupLeadsBySource,
  inRange,
  leadSeries,
  normalizeLead,
  socialSeries,
  socialSummary,
  stageBreakdown,
  summarizeAds,
  summarizeLeads,
  summarizeWebsite,
  toDate,
  websiteSeries,
} from '../utils/marketingReportMetrics';

// ── Palette ─────────────────────────────────────────────────────────────────
const C = {
  leads: '#DC2626',
  qualified: '#2563EB',
  clients: '#16A34A',
  open: '#F87171',
  lost: '#9CA3AF',
  visitors: '#2563EB',
  sessions: '#8B5CF6',
  spend: '#F59E0B',
  clicks: '#DC2626',
};

const STAGE_COLORS = {
  Prospects: '#EF4444',
  'Lead Received': '#3B82F6',
  'Discovery Call': '#8B5CF6',
  Assessment: '#10B981',
  'Onboarding Call': '#F59E0B',
  Proposal: '#EC4899',
  Client: '#16A34A',
  'Former client': '#65A30D',
  Lost: '#9CA3AF',
};

const PLATFORM_META = {
  Facebook: { icon: '/facebook.svg', color: '#1877F2', href: 'https://www.facebook.com/p/Builders-Breakthrough-Advisors-61556185584027/' },
  Instagram: { icon: '/instagram.svg', color: '#E1306C', href: 'https://www.instagram.com/buildersbreakthroughadvisors/?hl=en' },
  LinkedIn: { icon: '/Linkedin.svg', color: '#0A66C2', href: 'https://www.linkedin.com/company/builders-breakthrough-advisors/?viewAsMember=true' },
  YouTube: { icon: '/Youtube.svg', color: '#FF0000', href: 'https://www.youtube.com/@BuildersBreakthroughAdvisors' },
  TikTok: { icon: '/tiktok.svg', color: '#111827', darkColor: '#E5E7EB', href: 'https://www.tiktok.com/@builders_breakthrough' },
};

// ── Formatting ──────────────────────────────────────────────────────────────
const fmtInt = (n) => Math.round(n || 0).toLocaleString();
const fmtMoney = (n, digits) => {
  const d = digits ?? (Math.abs(n) < 100 ? 2 : 0);
  return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};
const fmtPct = (fraction, digits = 1) => `${((fraction || 0) * 100).toFixed(digits)}%`;
const fmtDuration = (sec) => {
  if (!sec) return '0s';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};
const fmtDate = (d) => (d ? format(d, 'MMM d, yyyy') : '—');

const emptySocialForm = () => ({
  date: format(new Date(), 'yyyy-MM-dd'),
  Facebook: '',
  Instagram: '',
  LinkedIn: '',
  YouTube: '',
  TikTok: '',
});

// ── Small building blocks ───────────────────────────────────────────────────

const CARD = 'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm';

/** Period-over-period pill. `invert` = lower is better (costs). */
const DeltaPill = ({ current, previous, invert = false }) => {
  if (previous == null) return null;
  if (!previous && !current) return <span className="text-[11px] text-gray-400">No change</span>;
  if (!previous) {
    return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">New</span>;
  }
  const pct = (current - previous) / previous;
  const up = pct >= 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        good
          ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
          : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
      }`}
      title={`Previous period: ${previous.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
    >
      <Icon className="w-3 h-3" />
      {Math.abs(pct * 100).toFixed(pct !== 0 && Math.abs(pct) < 0.1 ? 1 : 0)}%
    </span>
  );
};

const KpiCard = ({ icon: Icon, tone, label, value, sub, current, previous, invert }) => (
  <div className={`${CARD} p-4 flex flex-col items-center text-center gap-1.5`}>
    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${tone}`}>
      <Icon className="w-4 h-4" />
    </span>
    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-2xl xl:text-3xl font-bold tracking-tight leading-none text-gray-900 dark:text-white">{value}</span>
    <div className="flex items-center gap-1.5 flex-wrap justify-center min-h-[20px]">
      <DeltaPill current={current} previous={previous} invert={invert} />
      {sub && <span className="text-[11px] text-gray-500 dark:text-gray-400">{sub}</span>}
    </div>
  </div>
);

const Section = ({ icon: Icon, title, subtitle, left, right, children, className = '' }) => (
  <section className={`${CARD} p-5 md:p-6 ${className}`}>
    <div className="relative mb-5">
      <div className={`flex flex-col items-center text-center min-h-[40px] justify-center ${left || right ? 'md:px-44' : ''}`}>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          {Icon && <Icon className="w-5 h-5 text-red-600" />}
          {title}
        </h2>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
      </div>
      {(left || right) && (
        <div className="mt-3 flex items-center justify-center gap-2 md:mt-0 md:absolute md:inset-x-0 md:top-0 md:justify-between md:pointer-events-none">
          <div className="md:pointer-events-auto">{left}</div>
          <div className="md:pointer-events-auto flex items-center gap-2">{right}</div>
        </div>
      )}
    </div>
    {children}
  </section>
);

/** Flat stat strip — no tinted tiles, just hairline dividers. */
const StatStrip = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 rounded-xl border border-gray-100 dark:border-gray-800 divide-x divide-y lg:divide-y-0 divide-gray-100 dark:divide-gray-800 mb-5 overflow-hidden">
    {items.map((it) => (
      <div key={it.label} className="px-3 py-3 text-center">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{it.label}</div>
        <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{it.value}</div>
        {it.hint && <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{it.hint}</div>}
      </div>
    ))}
  </div>
);

const IconButton = ({ onClick, title, tone = 'gray', children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className={`p-2 rounded-xl border transition-colors ${
      tone === 'red'
        ? 'border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`}
  >
    {children}
  </button>
);

const EmptyChart = ({ children, className = 'h-72' }) => (
  <div className={`flex flex-col items-center justify-center text-center text-sm text-gray-500 dark:text-gray-400 rounded-xl border border-dashed border-gray-200 dark:border-gray-800 ${className}`}>
    {children}
  </div>
);

/** Tailwind-styled tooltip so it follows dark mode without inline styles. */
const ChartTooltip = ({ active, payload, label, formatters = {}, labelKey = 'tooltipLabel' }) => {
  if (!active || !payload?.length) return null;
  const title = payload[0]?.payload?.[labelKey] || label;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur px-3 py-2 shadow-lg text-xs">
      <div className="font-semibold text-gray-900 dark:text-white mb-1">{title}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || p.stroke || p.fill }} />
            {p.name}
          </span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {(formatters[p.dataKey] || fmtInt)(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const LegendDot = ({ color, label, value, dashed }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
    {dashed ? (
      <span className="w-4 border-t-2 border-dashed" style={{ borderColor: color }} />
    ) : (
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
    )}
    {label}
    {value != null && <span className="font-semibold text-gray-900 dark:text-white">{value}</span>}
  </span>
);

const Segmented = ({ options, value, onChange, size = 'md' }) => (
  <div className="inline-flex flex-wrap justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 shadow-sm gap-0.5">
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        onClick={() => onChange(o.key)}
        className={`rounded-lg font-medium transition-colors ${size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} ${
          value === o.key
            ? 'bg-red-600 text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div
      className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full ${wide ? 'max-w-3xl' : 'max-w-md'} max-h-[85vh] overflow-y-auto p-6`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative flex items-center justify-center mb-5">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">{title}</h3>
        <button onClick={onClose} className="absolute right-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

// ── Page ────────────────────────────────────────────────────────────────────

const MarketingReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [timeframe, setTimeframe] = useState('year');
  const [sourceMode, setSourceMode] = useState('source');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const [leads, setLeads] = useState([]);
  const [websiteRows, setWebsiteRows] = useState([]);
  const [adRows, setAdRows] = useState([]);
  const [socialEntries, setSocialEntries] = useState([]);
  const [newsletterCampaigns, setNewsletterCampaigns] = useState([]);

  const [showSocialPopup, setShowSocialPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [socialFormData, setSocialFormData] = useState(emptySocialForm);
  const [editingData, setEditingData] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      setError(null);
      // Leads + clients (conversion is in place on the same doc) and every
      // marketingReports row — website/ads are written globally by webhooks and
      // social snapshots must be visible to every internal user, not just the
      // person who typed them in.
      const [accountsSnap, reportsSnap, newsletter] = await Promise.all([
        getDocs(query(collection(db, 'accounts'), where('type', 'in', ['Leads', 'Clients']))),
        getDocs(collection(db, 'marketingReports')),
        getSharedNewsletterData().catch(() => ({ campaigns: [] })),
      ]);

      setLeads(accountsSnap.docs.map((d) => normalizeLead({ id: d.id, ...d.data() })));

      const rows = reportsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setWebsiteRows(dedupeDaily(rows.filter((r) => r.dataType === DATA_TYPES.WEBSITE)));
      setAdRows(dedupeDaily(rows.filter((r) => r.dataType === DATA_TYPES.ADVERTISING)));
      setSocialEntries(rows.filter((r) => r.dataType === DATA_TYPES.SOCIAL));
      setNewsletterCampaigns(newsletter.campaigns || []);
      setLoadedAt(new Date());
    } catch (err) {
      console.error('Error loading marketing report data:', err);
      setError('Failed to load marketing data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived metrics ──
  const datedLeads = useMemo(() => leads.filter((l) => l.createdAt), [leads]);
  const undatedLeadCount = leads.length - datedLeads.length;

  const range = useMemo(() => {
    const earliest = datedLeads.reduce((min, l) => (!min || l.createdAt < min ? l.createdAt : min), null);
    return getRange(timeframe, new Date(), earliest);
  }, [timeframe, datedLeads]);

  const unit = bucketUnit(range.start, range.end);
  const hasPrev = !!range.prevStart;

  const leadsNow = useMemo(() => datedLeads.filter((l) => inRange(l.createdAt, range.start, range.end)), [datedLeads, range]);
  const leadsPrev = useMemo(
    () => (hasPrev ? datedLeads.filter((l) => inRange(l.createdAt, range.prevStart, range.prevEnd)) : []),
    [datedLeads, range, hasPrev],
  );
  const leadStats = useMemo(() => summarizeLeads(leadsNow), [leadsNow]);
  const leadStatsPrev = useMemo(() => summarizeLeads(leadsPrev), [leadsPrev]);
  const leadChart = useMemo(() => leadSeries(leadsNow, range.start, range.end, unit), [leadsNow, range, unit]);
  const avgLeadsPerBucket = leadChart.length ? leadStats.total / leadChart.length : 0;
  const sourceRows = useMemo(() => groupLeadsBySource(leadsNow, sourceMode), [leadsNow, sourceMode]);
  const stages = useMemo(() => stageBreakdown(leadsNow), [leadsNow]);
  const recentLeads = useMemo(
    () => [...leadsNow].sort((a, b) => b.createdAt - a.createdAt).slice(0, 8),
    [leadsNow],
  );
  const peakBucket = useMemo(
    () => leadChart.reduce((best, b) => (b.leads > (best?.leads || 0) ? b : best), null),
    [leadChart],
  );

  const webNow = useMemo(() => websiteRows.filter((r) => inRange(r.day, range.start, range.end)), [websiteRows, range]);
  const webPrev = useMemo(
    () => (hasPrev ? websiteRows.filter((r) => inRange(r.day, range.prevStart, range.prevEnd)) : []),
    [websiteRows, range, hasPrev],
  );
  const web = useMemo(() => summarizeWebsite(webNow), [webNow]);
  const webPrevStats = useMemo(() => summarizeWebsite(webPrev), [webPrev]);
  const webChart = useMemo(() => websiteSeries(webNow, range.start, range.end, unit), [webNow, range, unit]);
  const lastWebDay = websiteRows.length ? websiteRows[websiteRows.length - 1].day : null;

  const adsNow = useMemo(() => adRows.filter((r) => inRange(r.day, range.start, range.end)), [adRows, range]);
  const adsPrev = useMemo(
    () => (hasPrev ? adRows.filter((r) => inRange(r.day, range.prevStart, range.prevEnd)) : []),
    [adRows, range, hasPrev],
  );
  const ads = useMemo(() => summarizeAds(adsNow), [adsNow]);
  const adsPrevStats = useMemo(() => summarizeAds(adsPrev), [adsPrev]);
  const adChart = useMemo(() => adSeries(adsNow, range.start, range.end, unit), [adsNow, range, unit]);
  const lastSpendRow = useMemo(() => [...adRows].reverse().find((r) => adSpendOf(r) > 0) || null, [adRows]);

  // Cost per Meta lead: ad spend only buys Facebook/Instagram/Meta leads, so
  // dividing by every lead (referrals, events…) would understate it.
  const isMetaLead = (l) => /facebook|instagram|meta/i.test(l.source) || channelFor(l.source) === 'Paid Social';
  const metaLeadsNow = leadsNow.filter(isMetaLead).length;
  const metaLeadsPrev = leadsPrev.filter(isMetaLead).length;
  const cplNow = ads.spend > 0 && metaLeadsNow > 0 ? ads.spend / metaLeadsNow : null;
  const cplPrev = adsPrevStats.spend > 0 && metaLeadsPrev > 0 ? adsPrevStats.spend / metaLeadsPrev : null;

  const social = useMemo(() => socialSummary(socialEntries, timeframe === 'all' ? null : range.start, range.end), [socialEntries, range, timeframe]);
  const socialChart = useMemo(() => socialSeries(socialEntries), [socialEntries]);
  const totalFollowers = social.reduce((s, p) => s + (p.followers || 0), 0);
  const followerChange = social.reduce((s, p) => s + (p.change || 0), 0);
  const lastSocialDay = social.reduce((max, p) => (p.asOf && (!max || p.asOf > max) ? p.asOf : max), null);

  const sentCampaigns = newsletterCampaigns.filter((c) => (parseInt(c.sent, 10) || 0) > 0);
  const avgMetric = (key) => (sentCampaigns.length
    ? sentCampaigns.reduce((s, c) => s + (parseFloat(c.metrics?.[key]) || 0), 0) / sentCampaigns.length
    : null);

  const rangeLabel = `${format(range.start, 'MMM d, yyyy')} – ${format(range.end, 'MMM d, yyyy')}`;
  const prevLabel = hasPrev ? `vs ${format(range.prevStart, 'MMM d')} – ${format(range.prevEnd, 'MMM d, yyyy')}` : null;
  const unitLabel = unit === 'day' ? 'daily' : unit === 'week' ? 'weekly' : 'monthly';
  const prev = (v) => (hasPrev ? v : null);

  const axisTick = { fontSize: 11, fill: darkMode ? '#9CA3AF' : '#6B7280' };
  const gridStroke = darkMode ? '#1F2937' : '#F3F4F6';
  const cursorFill = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';

  // ── Social entry handlers ──
  const handleSocialSubmit = async (e) => {
    e.preventDefault();
    const filled = SOCIAL_PLATFORMS.filter((p) => String(socialFormData[p] ?? '').trim() !== '');
    if (!filled.length) {
      setError('Enter a follower count for at least one platform.');
      return;
    }
    try {
      const day = toDate(socialFormData.date);
      const month = format(day, 'MMMM');
      const year = format(day, 'yyyy');
      const created = await Promise.all(filled.map((platform) => addMarketingReportEntry(DATA_TYPES.SOCIAL, {
        platform,
        followers: String(socialFormData[platform]).trim(),
        date: socialFormData.date,
        month,
        year,
      })));
      setSocialEntries((prevEntries) => [...created, ...prevEntries]);
      setError(null);
      setShowSocialPopup(false);
      setSocialFormData(emptySocialForm());
    } catch (err) {
      console.error('Error saving social data:', err);
      setError('Failed to save social data. Please try again.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // eslint-disable-next-line no-unused-vars
      const { id, userId, dataType, createdAt, updatedAt, ...updateData } = editingData;
      await updateMarketingReportEntry(id, updateData);
      setSocialEntries((prevEntries) => prevEntries.map((x) => (x.id === id ? { ...x, ...updateData } : x)));
      setEditingData(null);
    } catch (err) {
      console.error('Error updating entry:', err);
      setError('Failed to update entry. Please try again.');
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Delete the ${entry.platform} snapshot from ${fmtDate(toDate(entry.date))}?`)) return;
    try {
      await deleteMarketingReportEntry(entry.id);
      setSocialEntries((prevEntries) => prevEntries.filter((x) => x.id !== entry.id));
    } catch (err) {
      console.error('Error deleting entry:', err);
      setError('Failed to delete entry. Please try again.');
    }
  };

  const sortedSocialEntries = useMemo(
    () => [...socialEntries].sort((a, b) => (toDate(b.date) || 0) - (toDate(a.date) || 0) || a.platform.localeCompare(b.platform)),
    [socialEntries],
  );

  const sourceChartHeight = Math.max(160, sourceRows.length * 38 + 30);

  return (
    <div className="min-h-screen p-4 md:p-6 relative">
      <PortalBackground />
      <div className="relative w-full space-y-6">
        {/* Header */}
        <div className="relative flex flex-col items-center gap-3">
          <div className="absolute left-0 top-0">
            <BackButton onClick={() => navigate('/crm/reports')} />
          </div>
          <div className="absolute right-0 top-0">
            <IconButton onClick={() => load({ silent: true })} title="Refresh data">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
          </div>
          <div className="text-center px-14">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Marketing Analytics</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {rangeLabel}
              {prevLabel && <span className="hidden sm:inline"> · {prevLabel}</span>}
            </p>
          </div>
          <Segmented options={TIMEFRAMES} value={timeframe} onChange={setTimeframe} />
        </div>

        {error && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300" role="alert">
            <span>{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss"><X className="w-4 h-4" /></button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-96">
            <BBALoader size={48} />
          </div>
        ) : (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <KpiCard
                icon={Users} tone="bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400"
                label="New Leads" value={fmtInt(leadStats.total)}
                current={leadStats.total} previous={prev(leadStatsPrev.total)}
              />
              <KpiCard
                icon={UserCheck} tone="bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400"
                label="Qualified" value={fmtInt(leadStats.qualified)}
                sub={`${fmtPct(leadStats.qualifiedRate, 0)} of leads`}
                current={leadStats.qualified} previous={prev(leadStatsPrev.qualified)}
              />
              <KpiCard
                icon={Target} tone="bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400"
                label="Became Clients" value={fmtInt(leadStats.clients)}
                sub={`${fmtPct(leadStats.conversionRate, 0)} conversion`}
                current={leadStats.clients} previous={prev(leadStatsPrev.clients)}
              />
              <KpiCard
                icon={Globe} tone="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400"
                label="Website Visitors" value={fmtInt(web.visitors)}
                sub={`${fmtPct(web.engagementRate, 0)} engaged`}
                current={web.visitors} previous={prev(webPrevStats.visitors)}
              />
              <KpiCard
                icon={DollarSign} tone="bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400"
                label="Ad Spend" value={fmtMoney(ads.spend)}
                sub={ads.clicks ? `${fmtMoney(ads.cpc)} / click` : undefined}
                current={ads.spend} previous={prev(adsPrevStats.spend)}
              />
              <KpiCard
                icon={TrendingUp} tone="bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400"
                label="Cost / Meta Lead" value={cplNow != null ? fmtMoney(cplNow) : '—'}
                sub={cplNow != null ? `${metaLeadsNow} Meta lead${metaLeadsNow === 1 ? '' : 's'}` : 'No paid spend'}
                current={cplNow} previous={cplNow != null && hasPrev ? cplPrev : null} invert
              />
            </div>

            {/* Total Leads — full width */}
            <Section
              icon={Users}
              title="Total Leads"
              subtitle={`New leads by created date, ${unitLabel}. Includes leads that have since become clients.`}
            >
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-4">
                <LegendDot color={C.leads} label="Leads" value={fmtInt(leadStats.total)} />
                <LegendDot color={C.qualified} label="Qualified" value={fmtInt(leadStats.qualified)} />
                <LegendDot color={C.clients} label="Became clients" value={fmtInt(leadStats.clients)} />
                <LegendDot color={darkMode ? '#6B7280' : '#9CA3AF'} dashed label={`Avg / ${unit}`} value={avgLeadsPerBucket.toFixed(1)} />
                {peakBucket && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Peak: <span className="font-semibold text-gray-900 dark:text-white">{peakBucket.tooltipLabel}</span> ({peakBucket.leads})
                  </span>
                )}
              </div>
              {leadStats.total > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={leadChart} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="mrLeadsFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={C.leads} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={C.leads} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="mrQualFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={C.qualified} stopOpacity={0.18} />
                          <stop offset="100%" stopColor={C.qualified} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke={gridStroke} />
                      <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} />
                      <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={40} />
                      <Tooltip content={<ChartTooltip />} cursor={{ stroke: darkMode ? '#374151' : '#E5E7EB' }} />
                      <ReferenceLine y={avgLeadsPerBucket} stroke={darkMode ? '#6B7280' : '#9CA3AF'} strokeDasharray="5 5" />
                      <Area type="monotone" dataKey="leads" name="Leads" stroke={C.leads} strokeWidth={2.5} fill="url(#mrLeadsFill)" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
                      <Area type="monotone" dataKey="qualified" name="Qualified" stroke={C.qualified} strokeWidth={2} fill="url(#mrQualFill)" dot={false} activeDot={{ r: 4 }} />
                      <Line type="monotone" dataKey="clients" name="Became clients" stroke={C.clients} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyChart className="h-80">No leads were created in this period.</EmptyChart>
              )}
              {undatedLeadCount > 0 && (
                <p className="mt-3 text-center text-[11px] text-gray-400">
                  {undatedLeadCount} account{undatedLeadCount === 1 ? ' has' : 's have'} no created date and {undatedLeadCount === 1 ? 'is' : 'are'} excluded.
                </p>
              )}
            </Section>

            {/* Lead Source — full width */}
            <Section
              icon={Megaphone}
              title="Lead Source"
              subtitle="Where this period's leads came from, split by where they are now"
              right={(
                <Segmented
                  size="sm"
                  value={sourceMode}
                  onChange={setSourceMode}
                  options={[{ key: 'source', label: 'Source' }, { key: 'channel', label: 'Channel' }]}
                />
              )}
            >
              {sourceRows.length > 0 ? (
                <>
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-3">
                    <LegendDot color={C.clients} label="Became client" />
                    <LegendDot color={C.open} label="Still open" />
                    <LegendDot color={C.lost} label="Lost" />
                  </div>
                  <div style={{ height: sourceChartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sourceRows} layout="vertical" margin={{ top: 0, right: 48, left: 0, bottom: 0 }} barCategoryGap={8}>
                        <CartesianGrid horizontal={false} stroke={gridStroke} />
                        <XAxis type="number" allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={210}
                          tick={{ ...axisTick, fontSize: 12, fill: darkMode ? '#D1D5DB' : '#374151' }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => (v.length > 30 ? `${v.slice(0, 29)}…` : v)}
                        />
                        <Tooltip
                          cursor={{ fill: cursorFill }}
                          content={<ChartTooltip labelKey="name" />}
                        />
                        <Bar dataKey="clients" name="Became client" stackId="s" fill={C.clients} />
                        <Bar dataKey="open" name="Still open" stackId="s" fill={C.open} />
                        <Bar dataKey="lost" name="Lost" stackId="s" fill={C.lost} radius={[0, 6, 6, 0]}>
                          <LabelList dataKey="leads" position="right" fill={darkMode ? '#E5E7EB' : '#374151'} fontSize={12} fontWeight={600} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="mt-6 overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-800">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800/60">
                        <tr>
                          <th className={TH_CLASS}>{sourceMode === 'channel' ? 'Channel' : 'Source'}</th>
                          <th className={`${TH_CLASS} text-right`}>Leads</th>
                          <th className={`${TH_CLASS} text-right`}>Share</th>
                          <th className={`${TH_CLASS} text-right`}>Qualified</th>
                          <th className={`${TH_CLASS} text-right`}>Clients</th>
                          <th className={`${TH_CLASS} text-right`}>Conversion</th>
                          <th className={`${TH_CLASS} text-right`}>Lost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sourceRows.map((r) => (
                          <tr key={r.name} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="px-2 sm:px-4 py-2.5 font-medium text-gray-900 dark:text-white">{r.name}</td>
                            <td className="px-2 sm:px-4 py-2.5 text-right text-gray-900 dark:text-white font-semibold">{r.leads}</td>
                            <td className="px-2 sm:px-4 py-2.5 text-right">
                              <div className="inline-flex items-center gap-2 justify-end">
                                <span className="hidden md:block w-20 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                                  <span className="block h-full rounded-full bg-red-500" style={{ width: `${r.share * 100}%` }} />
                                </span>
                                <span className="text-gray-600 dark:text-gray-300 w-12">{fmtPct(r.share, 0)}</span>
                              </div>
                            </td>
                            <td className="px-2 sm:px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">{r.qualified} <span className="text-gray-400">({fmtPct(r.qualifiedRate, 0)})</span></td>
                            <td className="px-2 sm:px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">{r.clients}</td>
                            <td className={`px-2 sm:px-4 py-2.5 text-right font-semibold ${r.conversionRate > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>{fmtPct(r.conversionRate, 0)}</td>
                            <td className="px-2 sm:px-4 py-2.5 text-right text-gray-500 dark:text-gray-400">{r.lost}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 dark:bg-gray-800/60">
                        <tr className="border-t border-gray-200 dark:border-gray-700 font-semibold text-gray-900 dark:text-white">
                          <td className="px-2 sm:px-4 py-2.5">Total</td>
                          <td className="px-2 sm:px-4 py-2.5 text-right">{leadStats.total}</td>
                          <td className="px-2 sm:px-4 py-2.5 text-right">100%</td>
                          <td className="px-2 sm:px-4 py-2.5 text-right">{leadStats.qualified} <span className="text-gray-400 font-normal">({fmtPct(leadStats.qualifiedRate, 0)})</span></td>
                          <td className="px-2 sm:px-4 py-2.5 text-right">{leadStats.clients}</td>
                          <td className="px-2 sm:px-4 py-2.5 text-right text-green-600 dark:text-green-400">{fmtPct(leadStats.conversionRate, 0)}</td>
                          <td className="px-2 sm:px-4 py-2.5 text-right">{leadStats.lost}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              ) : (
                <EmptyChart className="h-48">No leads were created in this period.</EmptyChart>
              )}
            </Section>

            {/* Pipeline + recent leads */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Section icon={TrendingUp} title="Where These Leads Are Now" subtitle="Current pipeline stage of leads created in this period">
                {stages.length > 0 ? (
                  <div className="space-y-2.5">
                    {stages.map((s) => {
                      const pct = leadStats.total ? s.count / leadStats.total : 0;
                      return (
                        <div key={s.stage} className="flex items-center gap-3">
                          <span className="w-32 shrink-0 text-sm text-gray-700 dark:text-gray-300 truncate">{s.stage}</span>
                          <div className="flex-1 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden">
                            <div
                              className="h-full rounded-lg transition-all"
                              style={{ width: `${Math.max(pct * 100, 2)}%`, backgroundColor: STAGE_COLORS[s.stage] || '#6B7280' }}
                            />
                          </div>
                          <span className="w-20 shrink-0 text-right text-sm">
                            <span className="font-semibold text-gray-900 dark:text-white">{s.count}</span>
                            <span className="text-gray-400 ml-1">{fmtPct(pct, 0)}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyChart className="h-48">No leads in this period.</EmptyChart>
                )}
              </Section>

              <Section icon={Clock} title="Latest Leads" subtitle="Most recent leads in this period">
                {recentLeads.length > 0 ? (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                    {recentLeads.map((l) => (
                      <li key={l.id}>
                        <button
                          type="button"
                          onClick={() => navigate(l.isClient ? `/crm/client/${l.id}` : `/crm/lead/${l.id}`)}
                          className="w-full flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                        >
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{l.name}</span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{l.source || 'Unattributed'} · {fmtDate(l.createdAt)}</span>
                          </span>
                          <span
                            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold"
                            style={{
                              color: STAGE_COLORS[l.lost ? 'Lost' : l.stage] || '#6B7280',
                              borderColor: `${STAGE_COLORS[l.lost ? 'Lost' : l.stage] || '#6B7280'}40`,
                              backgroundColor: `${STAGE_COLORS[l.lost ? 'Lost' : l.stage] || '#6B7280'}14`,
                            }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {l.lost ? 'Lost' : l.stage}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyChart className="h-48">No leads in this period.</EmptyChart>
                )}
              </Section>
            </div>

            {/* Website */}
            <Section
              icon={Globe}
              title="Website & Organic Traffic"
              subtitle={`Google Analytics, synced daily${lastWebDay ? ` · latest data ${fmtDate(lastWebDay)}` : ''}`}
            >
              <StatStrip
                items={[
                  { label: 'Visitors', value: fmtInt(web.visitors), hint: hasPrev ? <DeltaPill current={web.visitors} previous={webPrevStats.visitors} /> : null },
                  { label: 'New Users', value: fmtInt(web.newUsers), hint: web.visitors ? `${fmtPct(web.newUsers / web.visitors, 0)} of visitors` : null },
                  { label: 'Sessions', value: fmtInt(web.sessions), hint: hasPrev ? <DeltaPill current={web.sessions} previous={webPrevStats.sessions} /> : null },
                  { label: 'Page Views', value: fmtInt(web.views), hint: web.sessions ? `${web.viewsPerSession.toFixed(1)} / session` : null },
                  { label: 'Engagement Rate', value: fmtPct(web.engagementRate), hint: `Bounce ${fmtPct(web.bounceRate)}` },
                  { label: 'Avg. Session', value: fmtDuration(web.avgSessionDuration), hint: `${web.days} day${web.days === 1 ? '' : 's'} of data` },
                ]}
              />
              {web.days > 0 ? (
                <>
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-3">
                    <LegendDot color={C.visitors} label="Visitors" />
                    <LegendDot color={C.sessions} label="Sessions" />
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={webChart} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                        <defs>
                          <linearGradient id="mrWebFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={C.visitors} stopOpacity={0.25} />
                            <stop offset="100%" stopColor={C.visitors} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke={gridStroke} />
                        <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} />
                        <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={44} />
                        <Tooltip content={<ChartTooltip />} cursor={{ stroke: darkMode ? '#374151' : '#E5E7EB' }} />
                        <Area type="monotone" dataKey="visitors" name="Visitors" stroke={C.visitors} strokeWidth={2.5} fill="url(#mrWebFill)" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
                        <Line type="monotone" dataKey="sessions" name="Sessions" stroke={C.sessions} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <EmptyChart>No website data was synced for this period.</EmptyChart>
              )}
            </Section>

            {/* Paid ads */}
            <Section
              icon={MousePointerClick}
              title="Paid Ads"
              subtitle="Meta Ads, synced daily"
            >
              <StatStrip
                items={[
                  { label: 'Spend', value: fmtMoney(ads.spend), hint: `${ads.activeDays} active day${ads.activeDays === 1 ? '' : 's'}` },
                  { label: 'Impressions', value: fmtInt(ads.impressions), hint: ads.impressions ? `${fmtMoney(ads.cpm)} CPM` : null },
                  { label: 'Clicks', value: fmtInt(ads.clicks), hint: hasPrev ? <DeltaPill current={ads.clicks} previous={adsPrevStats.clicks} /> : null },
                  { label: 'CTR', value: fmtPct(ads.ctr, 2), hint: 'clicks ÷ impressions' },
                  { label: 'Cost / Click', value: ads.clicks ? fmtMoney(ads.cpc) : '—', hint: 'spend ÷ clicks' },
                  { label: 'Cost / Meta Lead', value: cplNow != null ? fmtMoney(cplNow) : '—', hint: `${metaLeadsNow} Facebook/Meta lead${metaLeadsNow === 1 ? '' : 's'}` },
                ]}
              />
              {ads.spend > 0 || ads.clicks > 0 ? (
                <>
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-3">
                    <LegendDot color={C.spend} label="Spend" />
                    <LegendDot color={C.clicks} label="Clicks" />
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={adChart} margin={{ top: 10, right: 0, left: -8, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke={gridStroke} />
                        <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} />
                        <YAxis yAxisId="spend" tick={axisTick} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `$${v}`} />
                        <YAxis yAxisId="clicks" orientation="right" allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={40} />
                        <Tooltip content={<ChartTooltip formatters={{ spend: (v) => fmtMoney(v, 2) }} />} cursor={{ fill: cursorFill }} />
                        <Bar yAxisId="spend" dataKey="spend" name="Spend" fill={C.spend} radius={[6, 6, 0, 0]} maxBarSize={36} />
                        <Line yAxisId="clicks" type="monotone" dataKey="clicks" name="Clicks" stroke={C.clicks} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <EmptyChart className="h-40">
                  <span>No ad spend recorded in this period.</span>
                  {lastSpendRow && (
                    <span className="mt-1 text-xs text-gray-400">Last spend: {fmtDate(lastSpendRow.day)} — switch to All Time to see past campaigns.</span>
                  )}
                </EmptyChart>
              )}
            </Section>

            {/* Social */}
            <Section
              icon={Share2}
              title="Social Media"
              subtitle={`Follower snapshots entered by the team${lastSocialDay ? ` · latest ${fmtDate(lastSocialDay)}` : ''}`}
              left={(
                <IconButton onClick={() => { setEditingData(null); setShowEditPopup(true); }} title="Manage entries">
                  <Pencil className="w-4 h-4" />
                </IconButton>
              )}
              right={(
                <IconButton onClick={() => setShowSocialPopup(true)} title="Add follower snapshot" tone="red">
                  <Plus className="w-4 h-4" />
                </IconButton>
              )}
            >
              <div className="text-center mb-5">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Total Followers</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{fmtInt(totalFollowers)}</div>
                {timeframe !== 'all' && followerChange !== 0 && (
                  <div className={`text-xs font-semibold mt-1 ${followerChange > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {followerChange > 0 ? '+' : ''}{fmtInt(followerChange)} this period
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                {social.map((p) => {
                  const meta = PLATFORM_META[p.platform];
                  return (
                    <a
                      key={p.platform}
                      href={meta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex flex-col items-center text-center rounded-xl border border-gray-100 dark:border-gray-800 p-4 hover:border-gray-200 dark:hover:border-gray-700 hover:shadow-sm transition-all"
                    >
                      <img src={meta.icon} alt="" className="w-9 h-9 mb-2" />
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{p.platform}</span>
                      <span className="text-xl font-bold text-gray-900 dark:text-white mt-1">{p.followers != null ? fmtInt(p.followers) : '—'}</span>
                      <span className="text-[11px] mt-0.5 min-h-[16px]">
                        {p.change != null && p.change !== 0 ? (
                          <span className={p.change > 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-red-600 dark:text-red-400 font-semibold'}>
                            {p.change > 0 ? '+' : ''}{fmtInt(p.change)}
                          </span>
                        ) : (
                          <span className="text-gray-400">{p.asOf ? `as of ${format(p.asOf, 'MMM d, yy')}` : 'No data'}</span>
                        )}
                      </span>
                    </a>
                  );
                })}
              </div>
              {socialChart.length > 1 ? (
                <>
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-3">
                    {SOCIAL_PLATFORMS.filter((p) => socialChart.some((r) => r[p] != null)).map((p) => (
                      <LegendDot key={p} color={darkMode && PLATFORM_META[p].darkColor ? PLATFORM_META[p].darkColor : PLATFORM_META[p].color} label={p} />
                    ))}
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={socialChart} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke={gridStroke} />
                        <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} />
                        <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={44} />
                        <Tooltip content={<ChartTooltip labelKey="label" />} cursor={{ stroke: darkMode ? '#374151' : '#E5E7EB' }} />
                        {SOCIAL_PLATFORMS.map((p) => (
                          <Line
                            key={p}
                            type="monotone"
                            dataKey={p}
                            name={p}
                            stroke={darkMode && PLATFORM_META[p].darkColor ? PLATFORM_META[p].darkColor : PLATFORM_META[p].color}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <EmptyChart className="h-32">Add follower snapshots over time to see growth trends.</EmptyChart>
              )}
            </Section>

            {/* Newsletter */}
            <Section icon={Mail} title="Newsletter" subtitle="Shared newsletter campaigns (all time)">
              <StatStrip
                items={[
                  { label: 'Campaigns', value: fmtInt(newsletterCampaigns.length) },
                  { label: 'Emails Sent', value: fmtInt(newsletterCampaigns.reduce((s, c) => s + (parseInt(c.sent, 10) || 0), 0)) },
                  { label: 'Avg. Open Rate', value: avgMetric('openRate') != null ? `${avgMetric('openRate').toFixed(1)}%` : '—' },
                  { label: 'Avg. Click Rate', value: avgMetric('clickRate') != null ? `${avgMetric('clickRate').toFixed(1)}%` : '—' },
                  { label: 'Avg. Bounce Rate', value: avgMetric('bounceRate') != null ? `${avgMetric('bounceRate').toFixed(1)}%` : '—' },
                  { label: 'Avg. Unsubscribe', value: avgMetric('unsubscribeRate') != null ? `${avgMetric('unsubscribeRate').toFixed(1)}%` : '—' },
                ]}
              />
              {newsletterCampaigns.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-800">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                      <tr>
                        <th className={TH_CLASS}>Campaign</th>
                        <th className={TH_CLASS}>Start</th>
                        <th className={TH_CLASS}>Status</th>
                        <th className={`${TH_CLASS} text-right`}>Sent</th>
                        <th className={`${TH_CLASS} text-right`}>Open</th>
                        <th className={`${TH_CLASS} text-right`}>Click</th>
                        <th className={`${TH_CLASS} text-right`}>Unsub.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...newsletterCampaigns]
                        .sort((a, b) => (toDate(b.startDate) || 0) - (toDate(a.startDate) || 0))
                        .map((c) => {
                          const sent = parseInt(c.sent, 10) || 0;
                          const metric = (k) => (sent > 0 ? `${(parseFloat(c.metrics?.[k]) || 0).toFixed(1)}%` : '—');
                          return (
                            <tr key={c.id} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="px-2 sm:px-4 py-2.5">
                                <div className="font-medium text-gray-900 dark:text-white">{c.name || 'Untitled campaign'}</div>
                                {c.description && <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">{c.description}</div>}
                              </td>
                              <td className="px-2 sm:px-4 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDate(toDate(c.startDate))}</td>
                              <td className="px-2 sm:px-4 py-2.5">
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 px-2.5 py-0.5 text-[11px] font-semibold capitalize text-gray-600 dark:text-gray-300">
                                  <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                                  {c.status || 'draft'}
                                </span>
                              </td>
                              <td className="px-2 sm:px-4 py-2.5 text-right text-gray-900 dark:text-white font-semibold">{fmtInt(sent)}</td>
                              <td className="px-2 sm:px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">{metric('openRate')}</td>
                              <td className="px-2 sm:px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">{metric('clickRate')}</td>
                              <td className="px-2 sm:px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">{metric('unsubscribeRate')}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyChart className="h-32">No newsletter campaigns yet.</EmptyChart>
              )}
              {newsletterCampaigns.length > 0 && sentCampaigns.length === 0 && (
                <p className="mt-3 text-center text-[11px] text-gray-400">Averages appear once a campaign has recorded sends.</p>
              )}
            </Section>

            {loadedAt && (
              <p className="text-center text-[11px] text-gray-400 pb-2">Data loaded {format(loadedAt, 'MMM d, yyyy h:mm a')}</p>
            )}
          </>
        )}

        {/* Add social snapshot */}
        {showSocialPopup && (
          <Modal title="Add Follower Snapshot" onClose={() => setShowSocialPopup(false)}>
            <form onSubmit={handleSocialSubmit} className="space-y-4">
              <div>
                <label className={LABEL_CLASS}>Date</label>
                <input
                  type="date"
                  value={socialFormData.date}
                  onChange={(e) => setSocialFormData({ ...socialFormData, date: e.target.value })}
                  className={`${FIELD_CLASS} px-3`}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {SOCIAL_PLATFORMS.map((p) => (
                  <div key={p} className={p === 'TikTok' ? 'col-span-2' : ''}>
                    <label className={`${LABEL_CLASS} flex items-center gap-1.5`}>
                      <img src={PLATFORM_META[p].icon} alt="" className="w-4 h-4" />
                      {p}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={socialFormData[p]}
                      onChange={(e) => setSocialFormData({ ...socialFormData, [p]: e.target.value })}
                      className={`${FIELD_CLASS} px-3`}
                      placeholder="Followers"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowSocialPopup(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                  Cancel
                </button>
                <button type="submit" className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700">
                  Save Snapshot
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* Manage social snapshots */}
        {showEditPopup && (
          <Modal title="Social Media Entries" wide onClose={() => { setShowEditPopup(false); setEditingData(null); }}>
            {sortedSocialEntries.length === 0 ? (
              <p className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">No entries yet. Add your first snapshot with the + button.</p>
            ) : (
              <ul className="space-y-2">
                {sortedSocialEntries.map((entry) => (
                  <li key={entry.id} className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                    {editingData?.id === entry.id ? (
                      <form onSubmit={handleEditSubmit} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                        <div>
                          <label className={LABEL_CLASS}>Platform</label>
                          <select
                            value={editingData.platform || ''}
                            onChange={(e) => setEditingData({ ...editingData, platform: e.target.value })}
                            className={`${FIELD_CLASS} px-3 py-2`}
                          >
                            {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Followers</label>
                          <input
                            type="number"
                            min="0"
                            value={editingData.followers ?? ''}
                            onChange={(e) => setEditingData({ ...editingData, followers: e.target.value })}
                            className={`${FIELD_CLASS} px-3 py-2`}
                            required
                          />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Date</label>
                          <input
                            type="date"
                            value={editingData.date || ''}
                            onChange={(e) => {
                              const d = toDate(e.target.value);
                              setEditingData({
                                ...editingData,
                                date: e.target.value,
                                month: d ? format(d, 'MMMM') : editingData.month,
                                year: d ? format(d, 'yyyy') : editingData.year,
                              });
                            }}
                            className={`${FIELD_CLASS} px-3 py-2`}
                            required
                          />
                        </div>
                        <div className="flex gap-2">
                          <button type="submit" className="flex-1 py-2 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700">Save</button>
                          <button type="button" onClick={() => setEditingData(null)} className="flex-1 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">Cancel</button>
                        </div>
                      </form>
                    ) : (
                      <div className="flex items-center gap-3">
                        {PLATFORM_META[entry.platform] && <img src={PLATFORM_META[entry.platform].icon} alt="" className="w-6 h-6" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{entry.platform}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {fmtInt(parseInt(entry.followers, 10) || 0)} followers · {fmtDate(toDate(entry.date))}
                          </div>
                        </div>
                        <button
                          onClick={() => setEditingData({ ...entry, date: entry.date || (toDate(entry.createdAt) ? format(toDate(entry.createdAt), 'yyyy-MM-dd') : '') })}
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                          aria-label="Edit entry"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEntry(entry)}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                          aria-label="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Modal>
        )}
      </div>
    </div>
  );
};

export default MarketingReports;
